import axios, { CancelTokenSource } from 'axios'
import { useEffect, useState } from 'react'

import { WbacService } from '@cinch/oxd-utils/requests/src'
import { StorageKeys } from '@cinch/oxd-utils/storage/src'
import * as Storage from '@cinch/oxd-utils/storage/src'

import { Site } from '../../types'

const useBooking = (customerPostcode: string, vehiclePriceOffered: number) => {
  const { token: storageToken } = Storage.get(StorageKeys.wbac)
  const serviceConfig = { headers: { Authorization: `JWT ${storageToken}` } }

  const fetchSite = (siteId: number) => WbacService.get(`/sites/${siteId}`, serviceConfig)
  const fetchSlots = (newPostcode: string, vehiclePrice: number, source: CancelTokenSource) =>
    WbacService.get(`/sites/appointmentslots/${newPostcode}/${vehiclePrice}`, {
      ...serviceConfig,
      cancelToken: source.token,
    })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSiteLoading, setIsSiteLoading] = useState<boolean>(true)
  const [sites, setSites] = useState<any>([])
  const [postcode, handlePostcodeChange] = useState<string>(customerPostcode)
  const [site, setSite] = useState<Site | undefined>(undefined)
  const [cachedSites, updateCachedSites] = useState<{ [key: string]: Site }>({})
  const [hasError, setHasError] = useState<boolean>(false)

  const handleSiteSelect = async (siteId: number) => {
    const cachedSite = cachedSites[siteId]
    if (cachedSite) {
      setSite(cachedSite)
    } else {
      setIsSiteLoading(true)

      try {
        const { data } = await fetchSite(siteId)
        setSite(data)
      } catch (error) {
        setHasError(true)
      } finally {
        setIsSiteLoading(false)
      }
    }
  }

  useEffect(() => {
    let unmounted = false
    const source = axios.CancelToken.source()

    if (!unmounted) {
      setIsLoading(true)
    }

    const getSlots = async () => {
      try {
        const { data } = await fetchSlots(postcode, vehiclePriceOffered, source)

        if (!!data.sites.length && !unmounted) {
          setSites(data.sites)
        } else if (!data.sites.length) {
          setHasError(true)
        }
      } catch (error) {
        setHasError(true)
      } finally {
        if (!unmounted) {
          setIsLoading(false)
        }
      }
    }

    getSlots()

    return () => {
      unmounted = true
      source.cancel('Cancelling in cleanup')
    }
  }, [postcode])

  useEffect(() => {
    if (sites.length) {
      handleSiteSelect(sites[0].siteId)
    }
  }, [sites])

  useEffect(() => {
    if (!site || !site.siteId) {
      return
    }
    updateCachedSites({
      ...cachedSites,
      [site.siteId]: site,
    })
  }, [site])

  return {
    isLoading,
    isSiteLoading,
    sites,
    site,
    handlePostcodeChange,
    handleSiteSelect,
    hasError,
  }
}

export default useBooking
